import { useMemo } from "react";

import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { ReportFilterState } from "../ui/ReportFilters";
import { useAllInvoices, useAllTime, useAllWorks } from "../useReportData";
import { computeReport, reportRanges } from "./reportEngine";
import { ReportSpec } from "./reportSpec";

/**
 * Loads what an AI-created report needs (same endpoints as the standard reports, so the
 * person only ever sees what they may see) and computes it.
 */
export const useCustomReportData = (
  spec: ReportSpec | null,
  filters: Pick<ReportFilterState, "userIds" | "clientIds" | "workTypeIds">,
) => {
  const access = useReportsAccess();
  const { current, previous } = useMemo(
    () => (spec ? reportRanges(spec) : { current: null, previous: null }),
    // Ranges only depend on these fields (and today).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spec?.range, spec?.compare, spec?.dateField],
  );
  const userIds = scopeUserIds(access, filters.userIds);
  const source = spec?.source;
  const billed =
    spec?.filters.billed === "unbilled"
      ? false
      : spec?.filters.billed === "billed"
      ? true
      : null;

  const timeQuery = {
    userIds,
    customerIds: filters.clientIds,
    workTypeIds: filters.workTypeIds,
    billed,
  };
  const time = useAllTime(
    { ...timeQuery, from: current?.from ?? null, to: current?.to ?? null },
    source !== "time",
  );
  const timeBefore = useAllTime(
    { ...timeQuery, from: previous?.from ?? null, to: previous?.to ?? null },
    source !== "time" || !previous,
  );

  const works = useAllWorks(
    {
      userIds,
      customerIds: filters.clientIds,
      worktypeIds: filters.workTypeIds,
      hideGreenStatus:
        spec?.filters.workStatus === "open" ||
        spec?.filters.workStatus === "overdue",
    },
    source !== "work",
  );

  // Invoices are filtered by invoice date on the server; by due date in the browser.
  const byInvoiceDate = spec?.dateField === "invoice_date";
  const invoiceQuery = {
    customerIds: filters.clientIds,
    userIds: filters.userIds,
  };
  const invoices = useAllInvoices(
    {
      ...invoiceQuery,
      from: byInvoiceDate ? current?.from ?? null : null,
      to: byInvoiceDate ? current?.to ?? null : null,
    },
    source !== "invoices" || !access.billing,
  );
  const invoicesBefore = useAllInvoices(
    {
      ...invoiceQuery,
      from: previous?.from ?? null,
      to: previous?.to ?? null,
    },
    source !== "invoices" || !access.billing || !previous || !byInvoiceDate,
  );

  const loaded =
    source === "time"
      ? { now: time, before: previous ? timeBefore : null }
      : source === "work"
      ? { now: works, before: null }
      : {
          now: invoices,
          before: previous && byInvoiceDate ? invoicesBefore : null,
        };
  const isLoading = !spec || loaded.now.isLoading || !!loaded.before?.isLoading;
  const isError = loaded.now.isError || !!loaded.before?.isError;

  const result = useMemo(() => {
    if (!spec || isLoading || isError) return null;
    const data =
      spec.source === "time"
        ? { time: time.data }
        : spec.source === "work"
        ? { work: works.data }
        : { invoices: invoices.data };
    const before =
      spec.source === "time"
        ? previous
          ? { time: timeBefore.data }
          : null
        : spec.source === "invoices" && previous && byInvoiceDate
        ? { invoices: invoicesBefore.data }
        : null;
    return computeReport(spec, data, before);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spec,
    isLoading,
    isError,
    time.data,
    timeBefore.data,
    works.data,
    invoices.data,
    invoicesBefore.data,
  ]);

  return { result, isLoading, isError, access };
};
